import { createRoot } from 'react-dom/client';
import { AppView } from '@/views/AppView';
import { ErrorBoundaryView } from '@/views/ErrorBoundaryView';
import { AppStore, TOKEN_AppStore } from '@/views/AppStore';
import { ud_di } from '@/app/di';
import { assertBootSubscribers } from '@/app/bootSubscribers';
import { root as container } from '@/infrastructure/container';
import { AppBus, TOKEN_IAppBus } from '@/services/AppBus';
import { TOKEN_BusNarrator } from '@/services/BusNarrator';
import { MetricsRegistry, TOKEN_IMetrics } from '@/services/Metrics';
import { RingBufferTraceSink, Trace, TOKEN_ITrace } from '@/services/Trace';
import { TOKEN_SessionGateway } from '@/services/SessionGateway';
import { InvariantError } from '@/domain/errors';
import './index.css';

ud_di();

// Telemetry window hooks, armed for e2e (Playwright sets navigator.webdriver) or an explicit
// ?telemetry=1. Real visitors get a disarmed page: no ring buffer, no globals. Armed before
// anything else resolves so the ring buffer captures the timeline from seq 0.
if (navigator.webdriver || new URLSearchParams(location.search).get('telemetry') === '1') {
  const ring = new RingBufferTraceSink();
  (container.resolve(TOKEN_ITrace) as Trace).addSink(ring);
  const w = window as unknown as { __telemetry?: RingBufferTraceSink; __metrics?: MetricsRegistry };
  w.__telemetry = ring;
  w.__metrics = container.resolve(TOKEN_IMetrics) as MetricsRegistry;
}

// Narrator first, before any store: an event is narrated before its handlers run, so synchronous
// nested publishes read in causal order on the timeline.
container.resolve(TOKEN_BusNarrator);

// Subscribers next. AppStore and SessionGateway both subscribe in their constructors; resolving
// them here is what makes those subscriptions exist at all, since the container is lazy
// (INVARIANTS rule 20). The gateway is named explicitly rather than left to arrive through
// AppStore's dependency list: the day the shell stops taking it, its CONNECTIVITY_CHANGED
// subscription would vanish with no test failing.
container.resolve(TOKEN_SessionGateway);
const app = container.resolve<AppStore>(TOKEN_AppStore);

// Fail loud if a required subscriber was never wired, instead of dropping its events at runtime.
// narratorHandlers=1 pairs with the narrator resolve above: it discounts the narrator's own
// subscription, so an event with no real store behind it still fails here rather than hiding.
assertBootSubscribers(container.resolve(TOKEN_IAppBus) as AppBus, undefined, 1);

// Publishers last. init() calls connectivity.start() and nav.start(), each of which publishes its
// initial state; both now land on live handlers.
void app.init();

const el = document.getElementById('root');
if (el === null) throw new InvariantError('root element missing');
createRoot(el).render(
  <ErrorBoundaryView>
    <AppView />
  </ErrorBoundaryView>,
);
